import type { HTMLAttributes, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { ListPagination } from '@/components/efs/ListPagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';

export interface MasterTableColumn<TRow> {
  key: string;
  header: ReactNode;
  cell: (row: TRow) => ReactNode;
  className?: string;
  align?: 'left' | 'right' | 'center';
}

interface MasterTableProps<TRow> {
  columns: MasterTableColumn<TRow>[];
  rows: TRow[];
  rowKey: (row: TRow) => string;
  rowProps?: (row: TRow, index: number) => HTMLAttributes<HTMLTableRowElement> | undefined;
  isLoading?: boolean;
  emptyMessage?: ReactNode;
  pagination?: {
    page: number;
    perPage: number;
    total: number;
    onPageChange: (page: number) => void;
    onPerPageChange?: (perPage: number) => void;
    pageSizeOptions?: number[];
  };
}

const alignClass = (align?: 'left' | 'right' | 'center'): string => {
  if (align === 'right') return 'text-right';
  if (align === 'center') return 'text-center';
  return 'text-left';
};

export const MasterTable = <TRow,>({
  columns,
  rows,
  rowKey,
  rowProps,
  isLoading,
  emptyMessage,
  pagination,
}: MasterTableProps<TRow>) => {
  const { t } = useTranslation();

  return (
    <div className="space-y-3">
      <div className="rounded-md border border-efs-border bg-efs-bg-panel">
        <Table>
          <TableHeader>
            <TableRow>
              {columns.map((col) => (
                <TableHead
                  key={col.key}
                  className={cn(alignClass(col.align), col.className)}
                  scope="col"
                >
                  {col.header}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-8 text-center text-efs-text-muted"
                >
                  {t('efs.common.messages.loading')}
                </TableCell>
              </TableRow>
            ) : rows.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  className="py-8 text-center text-efs-text-muted"
                >
                  {emptyMessage ?? t('efs.common.messages.empty')}
                </TableCell>
              </TableRow>
            ) : (
              rows.map((row, rowIndex) => (
                <TableRow key={rowKey(row)} {...rowProps?.(row, rowIndex)}>
                  {columns.map((col) => (
                    <TableCell key={col.key} className={cn(alignClass(col.align), col.className)}>
                      {col.cell(row)}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {pagination ? (
        <ListPagination
          page={pagination.page}
          perPage={pagination.perPage}
          total={pagination.total}
          onPageChange={pagination.onPageChange}
          onPerPageChange={pagination.onPerPageChange}
          pageSizeOptions={pagination.pageSizeOptions}
        />
      ) : null}
    </div>
  );
};
